// [S-715/E-DRAFT-CITATIONS] Pure HTML transform: wraps each citation's
// drafted-text anchor in a `<mark class="cs-citation" data-citation-idx="N">`
// so CKEditor's already-configured General Html Support (arbitrary
// elements/attributes/classes allowed — see TemplateEditor.ckeditor.tsx)
// carries the mark through the model and round-trips it unchanged on save.
// No CKEditor import here on purpose: this is engine-agnostic string logic,
// kept out of the heavy lazy-loaded CKEditor chunk and unit-testable without it.
import type { DraftCitation } from "@csai/core";

/** Splits `html` on tag boundaries and marks each citation's FIRST
 *  occurrence, globally, in the text content only — never inside a tag (an
 *  attribute value, a class name). Citations with no findable anchor are
 *  silently left unmarked (the underlying data still exists in `citations`
 *  for the audit trail / S-717 — a missing visual mark isn't data loss). */
export function markCitations(html: string, citations: DraftCitation[]): string {
  // [traceability 2026-10-20] A draft filled from a template carries its own
  // tags, written as each value was placed (the source is known there, not
  // guessed from text). Matching anchors again would double-mark it.
  if (html.includes("data-citation-idx=")) return html;
  // [citation bug] One mark per anchor TEXT: the generator emits a citation per
  // occurrence, and each used to mark the next match — every "11" lit up.
  const seen = new Set<string>();
  const candidates = citations
    .map((c, i) => ({ anchor: c.anchor, i }))
    .filter((c) => {
      const key = c.anchor.trim();
      if (key.length === 0 || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  if (candidates.length === 0) return html;
  const used = new Set<number>();
  return html
    .split(/(<[^>]+>)/)
    .map((token) =>
      token.startsWith("<") ? token : markTextToken(token, candidates, used)
    )
    .join("");
}

function markTextToken(
  text: string,
  citations: Array<{ anchor: string; i: number }>,
  used: Set<number>
): string {
  let best: { start: number; end: number; idx: number } | null = null;
  for (const c of citations) {
    if (used.has(c.i)) continue;
    const found = findAnchor(text, c.anchor);
    if (found !== null && (best === null || found.start < best.start)) {
      best = { ...found, idx: c.i };
    }
  }
  if (best === null) return text;
  used.add(best.idx);
  const before = text.slice(0, best.start);
  const matched = text.slice(best.start, best.end);
  const after = text.slice(best.end);
  return (
    before +
    `<mark class="cs-citation" data-citation-idx="${best.idx}">${matched}</mark>` +
    markTextToken(after, citations, used)
  );
}

/** Exact literal match first (the common case — the generator's `anchor` is
 *  supposed to be verbatim). Falls back to a $/,/whitespace-insensitive
 *  search for digit-bearing anchors — mirrors VertexDraftGenerator's
 *  normalizeForMatch fidelity check, which exists because a bare-digit
 *  param ("500000") routinely renders money-formatted in the actual draft
 *  ("$500,000"); marks the REAL matched span, not the literal anchor. */
function findAnchor(
  text: string,
  anchor: string
): { start: number; end: number } | null {
  const exact = firstStandalone(text, new RegExp(escapeRegExp(anchor), "g"));
  if (exact !== null) return exact;
  // The draft HTML escapes `&`, quotes and angle brackets in text, while the
  // anchor is plain text — "Smith & Co" lives in the markup as "Smith &amp; Co".
  for (const variant of entityVariants(anchor)) {
    const hit = firstStandalone(text, new RegExp(escapeRegExp(variant), "g"));
    if (hit !== null) return hit;
  }
  if (!/\d/.test(anchor)) return null;
  const pattern = anchor
    .split("")
    .map((ch) => (/[$,\s]/.test(ch) ? "" : escapeRegExp(ch)))
    .join("[$,\\s]*");
  if (pattern.length === 0) return null;
  return firstStandalone(text, new RegExp(pattern, "g"));
}

/** The first match that stands alone: not inside a longer number or word
 *  ("10" in "$100,000" or "2010"), and not a clause number written as text
 *  ("10. NEXT STARTING DATE" in a draft from before numbered lists). */
function firstStandalone(text: string, re: RegExp): { start: number; end: number } | null {
  for (const m of text.matchAll(re)) {
    if (m[0].length === 0) continue;
    const start = m.index;
    const end = start + m[0].length;
    const before = text.slice(0, start);
    const after = text.slice(end);
    if (/[\p{L}\p{N}]$/u.test(before) || /^[\p{L}\p{N}]/u.test(after)) continue;
    if (/\d$/.test(m[0]) && /^[.,]\d/.test(after)) continue;
    if (/^\d+$/.test(m[0]) && /^\s*$/.test(before) && /^\.\s/.test(after)) continue;
    return { start, end };
  }
  return null;
}

/** The anchor as the draft's HTML would carry it, when that differs. */
function entityVariants(anchor: string): string[] {
  if (!/[&"'<>]/.test(anchor)) return [];
  const base = anchor.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return [base, base.replace(/'/g, "&#39;"), base.replace(/'/g, "&#x27;")];
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
